// Production → Audits: BOM audit + Time Study behind one mode slider.
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { ManufacturingBomAuditPanel } from "./ManufacturingBomAuditPanel";
import { ManufacturingTimeStudyPanel } from "./ManufacturingTimeStudyPanel";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import {
  MFG_BOM_AUDIT_ROLES,
  MFG_TIME_STUDY_ROLES,
} from "@/lib/roles/turboManufacturingRoles";

export type MfgAuditsLane = "bom" | "time_study";

const AUDITS_LANE_STORAGE_KEY = "bb-mfg-audits-lane";

const LANE_TABS: ReadonlyArray<{ id: MfgAuditsLane; label: string }> = [
  { id: "bom", label: "BOM" },
  { id: "time_study", label: "Time Study" },
];

function readStoredLane(): MfgAuditsLane | null {
  try {
    const v = sessionStorage.getItem(AUDITS_LANE_STORAGE_KEY);
    if (v === "bom" || v === "time_study") return v;
  } catch {
    /* ignore */
  }
  return null;
}

/** Prefer BOM when Models jumps here; otherwise restore last lane. */
export function preferMfgAuditsBomLane(): void {
  try {
    sessionStorage.setItem(AUDITS_LANE_STORAGE_KEY, "bom");
  } catch {
    /* ignore */
  }
}

export function ManufacturingAuditsPanel() {
  const gate = useManufacturingEntitlementGate();
  const auth = useAuth();
  const canBom =
    auth.status === "ready" && auth.hasRole(...MFG_BOM_AUDIT_ROLES);
  const canTimeStudy =
    auth.status === "ready" && auth.hasRole(...MFG_TIME_STUDY_ROLES);

  const availableLanes = useMemo(() => {
    return LANE_TABS.filter((t) => (t.id === "bom" ? canBom : canTimeStudy));
  }, [canBom, canTimeStudy]);

  const [lane, setLane] = useState<MfgAuditsLane>(() => {
    const stored = readStoredLane();
    if (stored === "bom" && canBom) return "bom";
    if (stored === "time_study" && canTimeStudy) return "time_study";
    if (canBom) return "bom";
    return "time_study";
  });
  const [sessionActive, setSessionActive] = useState(false);

  useEffect(() => {
    if (availableLanes.length === 0) return;
    if (!availableLanes.some((t) => t.id === lane)) {
      setLane(availableLanes[0]!.id);
      setSessionActive(false);
    }
  }, [availableLanes, lane]);

  useEffect(() => {
    try {
      sessionStorage.setItem(AUDITS_LANE_STORAGE_KEY, lane);
    } catch {
      /* ignore */
    }
  }, [lane]);

  if (!gate.enabled) {
    return (
      <div className="p-4">
        <ManufacturingGateBanner gate={gate} />
      </div>
    );
  }

  if (availableLanes.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-600 dark:text-zinc-400">
        Audits requires a Time Study or BOM audit role.
      </div>
    );
  }

  const sliderLocked = sessionActive && availableLanes.length > 1;

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Audits</h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
            BOM parts checks and Time Study labor capture
          </p>
        </div>
        {availableLanes.length > 1 ? (
          <div className="flex flex-col items-end gap-1">
            <div
              role="tablist"
              aria-label="Audit type"
              className="inline-flex flex-nowrap overflow-x-auto rounded-lg bg-gray-100 p-0.5 text-sm dark:bg-zinc-800"
            >
              {availableLanes.map((tab) => {
                const active = tab.id === lane;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={active}
                    type="button"
                    disabled={sliderLocked && !active}
                    title={
                      sliderLocked && !active
                        ? "Finish or leave the active audit to switch"
                        : undefined
                    }
                    onClick={() => {
                      if (sliderLocked) return;
                      setSessionActive(false);
                      setLane(tab.id);
                    }}
                    className={
                      "shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-sm font-semibold transition-colors " +
                      (active
                        ? "bg-white text-gray-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100"
                        : sliderLocked
                          ? "cursor-not-allowed text-gray-400 dark:text-zinc-600"
                          : "text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-200")
                    }
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            {sliderLocked ? (
              <span className="text-[11px] text-amber-700 dark:text-amber-300">
                Audit in progress — slider locked
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {lane === "bom" && canBom ? (
        <ManufacturingBomAuditPanel
          embedded
          onSessionActiveChange={setSessionActive}
        />
      ) : null}
      {lane === "time_study" && canTimeStudy ? (
        <ManufacturingTimeStudyPanel
          embedded
          onSessionActiveChange={setSessionActive}
        />
      ) : null}
    </div>
  );
}
